import type { Build } from '@todesktop/shared';
import { Box, Text } from 'ink';
import { type ReactNode, useState } from 'react';
import { hasBuildKickedOff } from '../utilities/buildStatus';
import logger from '../utilities/logger';
import useInput from '../utilities/useInput';
import CancelBuild, { useCancellation } from './CancelBuild';
import Footer from './Footer';
import Header from './Header';

export default function MainLayout({
  abortController,
  appId,
  appName,
  appVersion,
  build,
  children,
  commandUsed,
  hasBuildEverFailed,
}: {
  abortController?: AbortController;
  appId: string | undefined;
  appName: string;
  appVersion?: string;
  build?: Build;
  children?: ReactNode;
  commandUsed?: string;
  hasBuildEverFailed?: boolean;
}) {
  const [wasCtrlCPressed, setWasCtrlCPressed] = useState(false);
  const cancellation = useCancellation({ abortController, appId, build });

  const hasKickedOff = hasBuildKickedOff(build);

  useInput(
    async (_input, key) => {
      if (key.escape && cancellation.canCancel) {
        logger.debug('MainLayout component: esc pressed, cancelling build');
        cancellation.cancel();
      }
    },
    {},
    {
      onCtrlCPressed: () => {
        setWasCtrlCPressed(true);
      },
    },
  );

  return (
    <>
      <Header build={build} name={appName} version={appVersion} />

      {cancellation.executed && build ? (
        <Box flexDirection="column" marginBottom={1}>
          <CancelBuild
            buildId={build.id}
            cancellation={cancellation}
            commandUsed={commandUsed}
          />
        </Box>
      ) : (
        children
      )}

      <Footer
        hasBuildEverFailed={hasBuildEverFailed}
        shouldShowCancelBuildInstructions={
          cancellation.canCancel && !wasCtrlCPressed
        }
        uiUrl={build?.url ?? undefined}
      />

      {wasCtrlCPressed && hasKickedOff && build ? (
        <>
          <Box flexDirection="column" marginTop={1}>
            <Text color="gray">
              The build will continue in the background. To view it, run:
            </Text>
          </Box>
          <Box>
            <Text bold color="gray">
              todesktop builds {build.id}
            </Text>
          </Box>
        </>
      ) : null}
    </>
  );
}
